<template>
  <!-- Toast Container -->
  <transition name="toast-fade">
    <div
      v-if="visible"
      class="fixed inset-0 z-[9999] flex items-start justify-center px-4 pt-4 sm:pt-6 pointer-events-none font-NotoSerifKhmer"
    >
      <!-- Toast Notification -->
      <div
        @mouseenter="stopAutoClose"
        @mouseleave="resumeAutoClose"
        class="pointer-events-auto relative overflow-hidden flex items-center justify-between gap-4 w-full max-w-[90vw] sm:w-auto sm:max-w-md px-5 py-4 rounded-3xl shadow-[0_8px_32px_0_rgba(31,38,135,0.37)] backdrop-blur-2xl border border-white/20 ring-1 ring-white/10 transition-all duration-500 ease-out group"
        :class="[
          isFailed
            ? 'bg-red-500/10 dark:bg-red-500/20'
            : 'bg-white/20 dark:bg-black/40'
        ]"
      >
        <!-- Ambient Color Glow (Background Gradient Mesh) -->
        <div 
            class="absolute -inset-1 blur-2xl opacity-20 pointer-events-none"
            :class="isFailed ? 'bg-gradient-to-r from-red-600 to-pink-600' : 'bg-gradient-to-r from-lime-400 to-emerald-600'"
        ></div>

        <!-- Glass Surface Highlight -->
        <div class="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent pointer-events-none rounded-3xl"></div>

        <!-- Content Wrapper -->
        <div class="relative z-10 flex items-center gap-3.5 overflow-hidden">
          <!-- Icon Sphere -->
          <div
            class="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full shadow-inner border border-white/10"
            :class="[
                isFailed 
                ? 'bg-gradient-to-br from-red-500 to-pink-600 text-white shadow-red-500/30' 
                : 'bg-gradient-to-br from-lime-400 to-emerald-600 text-white shadow-emerald-500/30'
            ]"
          >
            <font-awesome-icon
              :icon="isFailed ? 'fa-solid fa-xmark' : 'fa-solid fa-check'"
              class="text-lg drop-shadow-md"
            />
          </div>
          <!-- Text -->
          <p class="text-[15px] sm:text-[16px] font-medium leading-snug tracking-wide text-white drop-shadow-sm "
             :class="isFailed ? 'text-red-50' : 'text-white'">
            {{ informMessage }}
          </p>
        </div>

        <!-- Close Button -->
        <button
          @click="closeToast"
          class="relative z-10 flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full text-white/60 hover:bg-white/10 hover:text-white transition-all duration-300 focus:outline-none backdrop-blur-sm"
        >
          <font-awesome-icon icon="fa-solid fa-times" class="text-sm" />
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
import { onMounted, ref, watch } from 'vue';

export default {
  props: ['informMessage', 'showToast', 'isFailed'],
  emits: ['onClose'],
  setup(props, { emit }) {
    const visible = ref(false);
    let autoCloseTimeout = null;
    const initialDuration = 3000;

    const remainingTime = ref(initialDuration);
    let startTimestamp;

    const startAutoClose = () => {
      if (autoCloseTimeout) clearTimeout(autoCloseTimeout);

      startTimestamp = Date.now();
      autoCloseTimeout = setTimeout(() => {
        closeToast();
      }, remainingTime.value);
    };

    const stopAutoClose = () => {
      if (autoCloseTimeout) {
        clearTimeout(autoCloseTimeout);
        remainingTime.value -= Date.now() - startTimestamp;
      }
    };

    const resumeAutoClose = () => {
      if (remainingTime.value > 0) {
        startAutoClose();
      }
    };

    const closeToast = () => {
      visible.value = false;
      if (autoCloseTimeout) clearTimeout(autoCloseTimeout);
      setTimeout(() => {
        emit('onClose');
      }, 400); // Matched closely to transition
    };

    watch(() => props.showToast, (newValue) => {
      if (newValue) {
        if (autoCloseTimeout) clearTimeout(autoCloseTimeout);
        remainingTime.value = initialDuration;
        visible.value = true;
        startAutoClose();
      }
    });

    onMounted(() => {
      if (props.showToast) {
        remainingTime.value = initialDuration;
        visible.value = true;
        startAutoClose();
      }
    });

    return {
      visible,
      stopAutoClose,
      resumeAutoClose,
      closeToast,
    };
  },
};
</script>

<style scoped>
.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: all 0.5s cubic-bezier(0.19, 1, 0.22, 1);
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translateY(-30px) scale(0.9) blur(10px);
}

.toast-fade-enter-to,
.toast-fade-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1) blur(0);
}
</style>